import imageCompression from 'browser-image-compression'

interface Options {
  maxSizeMB?: number
  maxWidthOrHeight?: number
  useWebWorker?: boolean
  maxIteration?: number
  exifOrientation?: number
  onProgress?: (progress: number) => void
  fileType?: string
  initialQuality?: number
  alwaysKeepResolution?: boolean
  signal?: AbortSignal
}

export const isVideo = (file: File | string): boolean => /video/i.test(typeof file === 'string' ? file : file.type)
export const isImage = (file: File | string): boolean => /image/i.test(typeof file === 'string' ? file : file.type)
export const isMediaFile = (file: File | string): boolean => isImage(file) || isVideo(file)

export const downloadFile = async (
  { url, name }: Record<'url' | 'name', string>,
  target: '_blank' | '_parent' | '_self' | '_top' = '_self'
) => {
  const downloadLink = document.createElement('a')

  downloadLink.target = target
  downloadLink.download = name
  downloadLink.href = url
  downloadLink.click()
}

export const downloadString = ({
  fileName,
  fileType,
  string,
}: {
  fileName: string
  fileType: string
  string: string
}) => {
  const blob = new Blob([string], { type: fileType })
  const a = document.createElement('a')

  a.download = fileName
  a.href = URL.createObjectURL(blob)

  document.body.append(a)

  a.click()
  a.remove()

  URL.revokeObjectURL(a.href)
}

export const downloadCsv = (string: string, entityName: string) => {
  downloadString({
    fileName: `Ignition ${entityName}, ${new Date().toDateString()}.csv`,
    fileType: 'text/csv',
    string,
  })
}

export const readFileAsBuffer = (file: File): Promise<FileReader['result']> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener('load', (ev) => resolve(ev.target?.result))
    reader.readAsArrayBuffer(file)
  })
}

export const convertFileListToArray = (fileList: FileList | DataTransferItemList): File[] =>
  Array.prototype.slice.call(fileList)

export const getFilesFromDragEvent = (event: DragEvent): File[] => {
  if (event.dataTransfer) {
    const dt = event.dataTransfer
    if (dt.files && dt.files.length > 0) {
      return convertFileListToArray(dt.files)
    }

    if (dt.items && dt.items.length > 0) {
      // During the drag even the dataTransfer.files is null
      // but Chrome implements some drag store, which is accesible via dataTransfer.items
      return convertFileListToArray(dt.items)
    }
  }
  return []
}

export const compressImage = async (imageFile: File, options?: Options) => {
  try {
    const compressedFile = await imageCompression(imageFile, { maxWidthOrHeight: 100, ...options })

    return compressedFile
  } catch (error) {
    console.error('Can not compress image', error)
    return null
  }
}
